/**
 * applyTheme — layers the theme cascade (Phase 2).
 *
 * EAA-1664: widens `applyCustomTheme`'s single global override slot into a
 * proper cascade — base tokens (design-tokens.css, already the default) →
 * app preset → instance default → tenant override, most specific wins. Each
 * layer is a **sparse** `CustomThemeConfig` that only specifies the tokens
 * it changes; a layer never needs to restate values it doesn't override.
 *
 * The tenant layer is the one injection surface (admin-editable at runtime),
 * so it is always sanitized here — the single choke point every app's
 * tenant-fetch flow funnels through — before any value reaches `:root`.
 */

import { applyCustomTheme, clearCustomTheme } from './theme-runtime';
import { sanitizeThemeConfig } from './themeValidation';
import type {
    CustomThemeColors,
    CustomThemeConfig,
    CustomThemeRadius,
    CustomThemeSkin,
    CustomThemeSkinTokens,
    CustomThemeTypography,
} from './types';

export interface ThemeLayers {
    /** Set by the app team, checked in (e.g. `apps/web-content/theme.config.ts`). */
    appPreset?: CustomThemeConfig | null;
    /** Baked at build/deploy time — env vars or a committed `theme.instance.json`. */
    instance?: CustomThemeConfig | null;
    /** Fetched at runtime from the tenant API, admin-editable — always sanitized. */
    tenant?: CustomThemeConfig | null;
}

export interface MergedTheme {
    colors: Partial<CustomThemeColors>;
    logoUrl?: string;
    borderRadius?: CustomThemeRadius;
    typography?: CustomThemeTypography;
    skin?: CustomThemeSkin;
    skinTokens?: CustomThemeSkinTokens;
}

/**
 * Merge the four cascade layers for a given color mode into one sparse
 * override set. Later layers win per-key — base is implicit (the CSS
 * defaults every layer is applied on top of), so only appPreset/instance/
 * tenant are merged here.
 */
export function mergeThemeLayers(layers: ThemeLayers, mode: 'light' | 'dark'): MergedTheme {
    const tenant = sanitizeThemeConfig(layers.tenant);
    const orderedLayers = [layers.appPreset, layers.instance, tenant];

    let colors: Partial<CustomThemeColors> = {};
    let logoUrl: string | undefined;
    let borderRadius: CustomThemeRadius | undefined;
    let typography: CustomThemeTypography | undefined;
    // EAA-1675: `skin` rides the same cascade as `logoUrl` — later layer wins.
    let skin: CustomThemeSkin | undefined;
    // EAA-1675: `skinTokens` merges sparsely per-key, same as `borderRadius`.
    let skinTokens: CustomThemeSkinTokens | undefined;

    for (const layer of orderedLayers) {
        if (!layer) continue;
        const modeColors = mode === 'dark' ? layer.dark : layer.light;
        if (modeColors) colors = { ...colors, ...modeColors };
        if (layer.logoUrl) logoUrl = layer.logoUrl;
        if (layer.borderRadius) borderRadius = { ...borderRadius, ...layer.borderRadius };
        if (layer.typography) typography = { ...typography, ...layer.typography };
        if (layer.skin) skin = layer.skin;
        if (layer.skinTokens) skinTokens = { ...skinTokens, ...layer.skinTokens };
    }

    return { colors, logoUrl, borderRadius, typography, skin, skinTokens };
}

const RADIUS_VAR_MAP: Record<keyof CustomThemeRadius, string> = {
    sm: '--radius-sm',
    md: '--radius-md',
    lg: '--radius-lg',
    xl: '--radius-xl',
    full: '--radius-full',
};

// EAA-1664 fix (Fable 5 review): clear radius vars before re-applying so a
// tenant/instance radius override doesn't survive switching to a session
// with no override (clearCustomTheme() only ever managed color vars).
function clearRadiusOverride(): void {
    const root = document.documentElement.style;
    for (const varName of Object.values(RADIUS_VAR_MAP)) {
        root.removeProperty(varName);
    }
    root.removeProperty('--radius');
}

function applyRadiusOverride(radius: CustomThemeRadius | undefined): void {
    if (!radius) return;
    const root = document.documentElement.style;
    for (const [key, varName] of Object.entries(RADIUS_VAR_MAP) as [keyof CustomThemeRadius, string][]) {
        const value = radius[key];
        if (!value) continue;
        root.setProperty(varName, value);
        // shadcn's single `--radius` scalar tracks the `md` step of the scale.
        if (key === 'md') root.setProperty('--radius', value);
    }
}

// EAA-1675: typography.fontFamily was merged but never actually applied —
// only returned to the caller. web-content still needs the raw value to
// merge into Ant's token config (additive), so MergedTheme keeps returning
// it; this also writes it to the CSS vars content-kit/content-core consume.
function applyTypography(typography: CustomThemeTypography | undefined): void {
    const root = document.documentElement.style;
    root.removeProperty('--font-body');
    root.removeProperty('--font-heading');
    if (typography?.fontFamily) {
        root.setProperty('--font-body', typography.fontFamily);
        root.setProperty('--font-heading', typography.fontFamily);
    }
}

const SKIN_ATTR = 'data-skin';

// EAA-1675: mirrors the EAA-1664 radius-reset fix above — clear before
// re-apply so a skin doesn't survive a switch to a tenant/session with none.
function clearSkin(): void {
    document.documentElement.removeAttribute(SKIN_ATTR);
}

function applySkin(skin: CustomThemeSkin | undefined): void {
    if (skin && skin !== 'default') {
        document.documentElement.setAttribute(SKIN_ATTR, skin);
    }
}

const SKIN_TOKEN_VAR_MAP: Record<keyof CustomThemeSkinTokens, string> = {
    btnDepth: '--btn-depth',
    borderWidthUi: '--border-width-ui',
    shadowRaised: '--shadow-raised',
    motionPress: '--motion-press',
    fontDisplay: '--font-display',
};

// EAA-1675: mirrors clearRadiusOverride — clear before re-apply so a
// tenant's skin token override doesn't survive switching to a session with
// none. Written as inline `:root` styles, so they win over the
// `[data-skin='playful']` stylesheet block per normal cascade precedence.
function clearSkinTokens(): void {
    const root = document.documentElement.style;
    for (const varName of Object.values(SKIN_TOKEN_VAR_MAP)) {
        root.removeProperty(varName);
    }
}

function applySkinTokens(tokens: CustomThemeSkinTokens | undefined): void {
    if (!tokens) return;
    const root = document.documentElement.style;
    for (const [key, varName] of Object.entries(SKIN_TOKEN_VAR_MAP) as [
        keyof CustomThemeSkinTokens,
        string,
    ][]) {
        const value = tokens[key];
        if (!value) continue;
        root.setProperty(varName, value);
    }
}

/**
 * Apply the merged theme cascade for the given mode by writing CSS custom
 * properties on `:root`. Colors reuse the existing `applyCustomTheme`
 * mechanism; radius and typography (`--font-body`/`--font-heading`) are
 * layered on top the same way. `logoUrl` is returned by `mergeThemeLayers`
 * for callers to expose through React context (e.g. rendering a tenant
 * logo) — it isn't a CSS custom property. `typography` is also returned so
 * web-content can additionally merge the font family into Ant's token
 * config. `skin` is stamped as `data-skin` on `<html>` so `skin.css`
 * selectors can match it.
 */
export function applyTheme(layers: ThemeLayers, mode: 'light' | 'dark'): MergedTheme {
    const merged = mergeThemeLayers(layers, mode);

    clearCustomTheme();
    clearRadiusOverride();
    clearSkin();
    clearSkinTokens();
    if (Object.keys(merged.colors).length > 0) {
        applyCustomTheme(merged.colors, mode);
    }
    applyRadiusOverride(merged.borderRadius);
    applyTypography(merged.typography);
    applySkin(merged.skin);
    applySkinTokens(merged.skinTokens);

    return merged;
}
